/**
 * Campos do modal de contato — C-75 + GUPM-202. Só apresentação: valor, erro e
 * dica por campo; o estado e as regras ficam em `useContatoForm`.
 *
 * GUPM-202: sai a escolha de categoria. Entram o "Usuário do app" (vinculado,
 * nome e e-mail ficam somente leitura), as funções como etiquetas e a marcação
 * "É de emergência". A empresa é sempre pedida, com sugestões das já cadastradas.
 */
import type { CampoContato, ContatoEntrada } from '@/domain/contatos/validacao';
import type { FormularioContato } from '@/hooks/useContatoForm';
import CampoFuncoes from './CampoFuncoes';
import CampoUsuario from './CampoUsuario';

type CampoTexto = Exclude<CampoContato, 'funcoes'>;

const ROTULOS: Record<CampoTexto, { rotulo: string; tipo?: string; dica?: string }> = {
  name: { rotulo: 'Nome' },
  email: { rotulo: 'E-mail', tipo: 'email' },
  empresa: { rotulo: 'Empresa', dica: 'Escolha uma existente ou digite uma nova' },
  phone: { rotulo: 'Telefone', tipo: 'tel', dica: 'Só números, espaço, +, ( ) e -' },
  display: { rotulo: 'Exibição', dica: 'Como o telefone aparece na tela. Vazio = o próprio telefone' },
  disponibilidade: { rotulo: 'Disponibilidade', dica: 'Opcional. Ex.: Plantão 24h' },
};

interface CampoProps {
  campo: CampoTexto;
  entrada: ContatoEntrada;
  erro?: string;
  somenteLeitura?: boolean;
  lista?: string;
  dica?: string;
  onChange: (campo: CampoTexto, valor: string) => void;
}

function Campo({ campo, entrada, erro, somenteLeitura, lista, dica, onChange }: CampoProps) {
  const { rotulo, tipo } = ROTULOS[campo];
  const id = `contato-${campo}`;
  const texto = dica ?? ROTULOS[campo].dica;
  return (
    <div>
      <label htmlFor={id} className="block text-[11px] font-bold text-jf-muted">{rotulo}</label>
      <input
        id={id}
        type={tipo ?? 'text'}
        list={lista}
        value={entrada[campo]}
        readOnly={somenteLeitura}
        onChange={(e) => onChange(campo, e.target.value)}
        aria-invalid={erro ? true : undefined}
        aria-describedby={erro ? `${id}-erro` : texto ? `${id}-dica` : undefined}
        className={`mt-0.5 w-full rounded-md border border-jf-border px-2 py-1.5 text-xs focus:border-jf-yellow focus:outline-none ${
          somenteLeitura ? 'bg-jf-surface text-jf-muted' : 'bg-jf-bg text-white'
        }`}
      />
      {erro ? (
        <p id={`${id}-erro`} className="mt-0.5 text-[11px] text-red-400">{erro}</p>
      ) : (
        texto && <p id={`${id}-dica`} className="mt-0.5 text-[10px] text-jf-dim">{texto}</p>
      )}
    </div>
  );
}

export default function CamposContato({ form, empresas }: { form: FormularioContato; empresas: readonly string[] }) {
  const { entrada, erros, vinculado } = form;
  const dicaVinculo = vinculado ? 'Vem do cadastro do usuário do app' : undefined;
  const props = { entrada, onChange: form.alterar };
  return (
    <div className="space-y-2.5">
      <CampoUsuario
        valor={entrada.app_user_id}
        usuarios={form.usuarios}
        nomeAtual={entrada.name}
        carregando={form.carregandoUsuarios}
        erro={form.erroUsuarios}
        onEscolher={form.vincular}
      />
      <Campo campo="name" erro={erros.name} somenteLeitura={vinculado} dica={dicaVinculo} {...props} />
      <Campo campo="email" erro={erros.email} somenteLeitura={vinculado} dica={dicaVinculo} {...props} />
      <CampoFuncoes
        funcoes={entrada.funcoes}
        digitada={form.funcaoDigitada}
        sugestoes={form.sugestoesFuncao}
        erro={erros.funcoes}
        onDigitar={form.setFuncaoDigitada}
        onAdicionar={form.adicionarFuncao}
        onRemover={form.removerFuncao}
      />
      <datalist id="contato-empresas">
        {empresas.map((e) => (
          <option key={e} value={e} />
        ))}
      </datalist>
      <Campo campo="empresa" erro={erros.empresa} lista="contato-empresas" {...props} />
      <div>
        <label className="flex items-center gap-2 text-xs text-white">
          <input
            type="checkbox"
            checked={entrada.is_emergencia}
            disabled={form.bloqueioDesmarcar !== null}
            onChange={(e) => form.marcarEmergencia(e.target.checked)}
            aria-describedby="contato-emergencia-dica"
          />
          É de emergência
        </label>
        <p id="contato-emergencia-dica" className="mt-0.5 text-[10px] text-jf-dim">
          {form.bloqueioDesmarcar ?? 'Aparece no botão 🚨, na Cobertura Técnica e no PAEBM.'}
        </p>
      </div>
      <Campo campo="phone" erro={erros.phone} {...props} />
      <Campo campo="display" erro={erros.display} {...props} />
      <Campo campo="disponibilidade" erro={erros.disponibilidade} {...props} />
    </div>
  );
}
